import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SEO } from '../components/seo/index.js';
import {
  InterviewSessionChrome,
  type InterviewSessionChromeHandle,
} from '../components/shared/SessionHeader.js';
import { StageTransitionOverlay } from '../components/interview/StageTransitionOverlay.js';
import { useSimulation } from '../hooks/useSimulation.js';
import { useSimulationStore } from '../store/simulationStore.js';

export function InterviewLayout() {
  const { t } = useTranslation();
  const sim = useSimulation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The EXPERIMENTAL one-call interview owns its whole screen: no stage-pill
  // chrome, no page scroll — a fixed, call-app shell like Teams/Zoom.
  const isCallRoute = /\/interview\/[^/]+\/call(\/|$)/.test(pathname);
  const skipLanguageCheck = useSimulationStore((s) => s.skipLanguageCheck);
  const chromeRef = useRef<InterviewSessionChromeHandle>(null);

  // Only guard active stage routes — not language-check, not feedback/complete screens.
  const blockBackNav = sim.sessionControlsEnabled && !sim.isLanguageCheckRoute && !sim.isStageComplete && !isCallRoute;

  // Back button (popstate) → show the existing "End this stage?" modal instead of leaving.
  // We push a sentinel entry so the first back-press fires popstate while we're still here,
  // then immediately re-push to absorb any subsequent presses while the modal is open.
  useEffect(() => {
    if (!blockBackNav) return;

    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      chromeRef.current?.requestEndStageConfirm();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [blockBackNav]);

  // Refresh / tab close / external navigation → native browser confirmation +
  // best-effort keepalive PATCH so the stage is marked complete if the user leaves.
  useEffect(() => {
    if (!blockBackNav) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required for legacy browsers; modern ones ignore the string but need the attribute set.
      event.returnValue = '';
      void sim.completeStageBestEffort();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [blockBackNav, sim.completeStageBestEffort]);

  const onExitInterview = () => {
    useSimulationStore.getState().reset();
    navigate('/dashboard');
  };

  const onSkipLanguageCheckConfirmed = sim.simulationId
    ? async () => {
        await skipLanguageCheck(sim.simulationId!);
        navigate(`/interview/${sim.simulationId}/stage/1`);
      }
    : undefined;

  if (isCallRoute) {
    return (
      <div className="flex h-dvh flex-col overflow-hidden bg-ig-bg-primary text-ig-text-primary">
        <SEO
          noindex
          title={t('interview.sessionTitle', { defaultValue: 'Interview session' })}
          description="Private InterviewGator interview session."
        />
        <Outlet />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-ig-bg-primary text-ig-text-primary">
      {sim.stageTransition ? (
        <StageTransitionOverlay
          kind="stage"
          stageNumber={sim.stageTransition.stageNumber}
          totalStages={sim.stageTransition.totalStages}
          skipped={sim.stageTransition.skipped}
        />
      ) : null}
      <SEO noindex title={t('interview.sessionTitle', { defaultValue: 'Interview session' })} description="Private InterviewGator interview session." />
      <div className="flex min-h-0 flex-1 flex-col">
        <InterviewSessionChrome
          ref={chromeRef}
          currentPhase={sim.currentPhase}
          phases={sim.phases}
          elapsedSeconds={sim.elapsedSeconds}
          onEndStage={sim.onEndStage}
          onEndStageConfirmed={() => void sim.endStageWithoutConfirm()}
          simulationConfig={sim.simulationConfig}
          controlsEnabled={sim.sessionControlsEnabled}
          endStageReady={sim.isStageComplete}
          progress={sim.progress}
          durationLabel={sim.staticDurationLabel}
          endActionLabel={sim.endActionLabel}
          questionCounterLabel={sim.questionCounterLabel}
          onExitInterview={onExitInterview}
          onSkipLanguageCheckConfirmed={onSkipLanguageCheckConfirmed}
        >
          <Outlet />
        </InterviewSessionChrome>
      </div>
    </div>
  );
}
